import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import type {Questionnaire} from '@common/models'
import {
  getMinimumDateOfBirthForAge,
  isAtLeastMinimumAge,
  MINIMUM_PATIENT_AGE,
} from '@common/utils'

import {
  isBookingDetailsFormValid,
  type BookingFormValues,
} from './bookingFormValidation'
import {BookingQuestion} from './BookingQuestion'
import {ReserveBar, BookButton} from './BookingsPortalPage.styled'
import {
  PreQuestionnaireRenderer,
  PreQuestionnaireAnswers,
} from './PreQuestionnaireRenderer'

import {Container} from '~/components/general/container/Container'
import {
  DOBField,
  isoToDOBParts,
  type DOBParts,
} from '~/components/general/dobField/DOBField'
import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {isEmailValid} from '~/utils/general'
import {
  validatePhoneNumberInput,
  translatePhoneValidationError,
} from '~/utils/phoneValidation'

export type {BookingFormValues}

type DetailsFormProps = {
  title: string
  form: BookingFormValues
  setForm: React.Dispatch<React.SetStateAction<BookingFormValues>>
  onValidityChange: (isValid: boolean) => void
  preQuestionnaire?: Questionnaire
  preqAnswers: PreQuestionnaireAnswers
  onChangePreqAnswer: (
    qid: string,
    ans: {value?: string; choices?: string[]},
  ) => void
  bookDisabled: boolean
  onClickBook: () => void
  bookLabel: string
}

export const DetailsForm: React.FC<DetailsFormProps> = ({
  title,
  form,
  setForm,
  onValidityChange,
  preQuestionnaire,
  preqAnswers,
  onChangePreqAnswer,
  bookDisabled,
  onClickBook,
  bookLabel,
}) => {
  const {t} = useTranslation<string>()
  const [emailTouched, setEmailTouched] = useState(false)
  const [dobParts, setDobParts] = useState<DOBParts>(() =>
    isoToDOBParts(form.dateOfBirth),
  )

  const emailInvalid = !!form.email.trim() && !isEmailValid(form.email)
  const phoneValidation = validatePhoneNumberInput(form.phone, {required: true})
  const maxDateOfBirth = getMinimumDateOfBirthForAge(MINIMUM_PATIENT_AGE)
  const dateOfBirthOk = isAtLeastMinimumAge(
    form.dateOfBirth,
    MINIMUM_PATIENT_AGE,
  )
  const dateOfBirthErrorMessage =
    form.dateOfBirth && !dateOfBirthOk
      ? t('You must be at least 16 years old to book.')
      : undefined
  const phoneErrorMessage =
    phoneValidation.error && form.phone.trim()
      ? translatePhoneValidationError(phoneValidation.error.code, t)
      : undefined

  const isValid = useMemo(() => isBookingDetailsFormValid(form), [form])

  useEffect(() => {
    onValidityChange(isValid)
  }, [isValid, onValidityChange])

  return (
    <Container direction="column" spacing={2}>
      <Typography weight="bold">{title}</Typography>
      <BookingQuestion title={t('First name')}>
        <Input
          value={form.firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({...f, firstName: e.target.value}))
          }
          required={!form.firstName.trim()}
          name="firstName"
          autoComplete="given-name"
        />
      </BookingQuestion>
      <BookingQuestion title={t('Second name')}>
        <Input
          value={form.secondName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({...f, secondName: e.target.value}))
          }
          required={!form.secondName.trim()}
          name="familyName"
          autoComplete="family-name"
        />
      </BookingQuestion>
      <BookingQuestion
        title={t('Personal email')}
        subtitle={t("We'll send your booking confirmation to this email.")}>
        <Input
          type="email"
          name="email"
          autoComplete="email"
          value={form.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({...f, email: e.target.value}))
          }
          onBlur={() => setEmailTouched(true)}
          errorMessage={emailTouched && emailInvalid ? t('Invalid email.') : ''}
          required={!form.email.trim() || (emailTouched && emailInvalid)}
        />
      </BookingQuestion>
      <BookingQuestion title={t('Phone')}>
        <Input
          type="tel"
          name="phoneNumber"
          autoComplete="tel"
          value={form.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({...f, phone: e.target.value}))
          }
          errorMessage={phoneErrorMessage}
          required={!form.phone.trim() || !!phoneValidation.error}
        />
      </BookingQuestion>
      <BookingQuestion title={t('Date of birth')}>
        <DOBField
          name="dateOfBirth"
          hideLabel
          value={dobParts}
          maxYear={Number(maxDateOfBirth.slice(0, 4))}
          maxDate={maxDateOfBirth}
          required={!dateOfBirthOk}
          errorMessage={dateOfBirthErrorMessage}
          onChange={({day, month, year, isoDate}) => {
            setDobParts({day, month, year})
            setForm((f) => ({...f, dateOfBirth: isoDate ?? ''}))
          }}
        />
      </BookingQuestion>

      {preQuestionnaire && preQuestionnaire.questions?.length > 0 && (
        <PreQuestionnaireRenderer
          questionnaire={preQuestionnaire}
          answers={preqAnswers}
          onChange={onChangePreqAnswer}
        />
      )}

      <ReserveBar>
        <BookButton
          color="secondary"
          disabled={bookDisabled}
          onClick={onClickBook}>
          {bookLabel}
        </BookButton>
      </ReserveBar>
    </Container>
  )
}
